import { Box } from '@mui/material';
import type { Editor } from '@tiptap/react';
import React from 'react';
import {
  INLINE_RICH_TEXT_PRESETS,
  type InlineRichTextButton,
  type InlineRichTextPreset,
} from './inlineRichTextPresets';

const buttonStyle = (active: boolean): React.CSSProperties => ({
  border: '1px solid #ccc',
  background: active ? '#d5d5d5' : '#fff',
  padding: '1px 7px',
  cursor: 'pointer',
  borderRadius: 3,
  fontSize: 12,
  lineHeight: 1.4,
});

function createButtons(editor: Editor): Record<InlineRichTextButton, () => React.ReactNode> {
  const button = (label: string, run: () => void, active: boolean, style?: React.CSSProperties) => (
    <button
      key={label}
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={run}
      style={{ ...buttonStyle(active), ...style }}
      title={label}
    >
      {label}
    </button>
  );

  const chain = () => editor.chain().focus();
  const heading = { fontWeight: 700 };

  return {
    bold: () =>
      button('B', () => chain().toggleBold().run(), editor.isActive('bold'), { fontWeight: 700 }),
    italic: () =>
      button('I', () => chain().toggleItalic().run(), editor.isActive('italic'), {
        fontStyle: 'italic',
      }),
    underline: () =>
      button('U', () => chain().toggleUnderline().run(), editor.isActive('underline'), {
        textDecoration: 'underline',
      }),
    subscript: () =>
      button('x₂', () => chain().toggleSubscript().run(), editor.isActive('subscript')),
    superscript: () =>
      button('x²', () => chain().toggleSuperscript().run(), editor.isActive('superscript')),
    bulletList: () =>
      button('• List', () => chain().toggleBulletList().run(), editor.isActive('bulletList')),
    orderedList: () =>
      button('1. List', () => chain().toggleOrderedList().run(), editor.isActive('orderedList')),
    h2: () =>
      button(
        'H2',
        () => chain().toggleHeading({ level: 2 }).run(),
        editor.isActive('heading', { level: 2 }),
        heading,
      ),
    h3: () =>
      button(
        'H3',
        () => chain().toggleHeading({ level: 3 }).run(),
        editor.isActive('heading', { level: 3 }),
        heading,
      ),
    link: () =>
      button(
        'Link',
        () => {
          const href = window.prompt('Enter the HTTPS URL');
          if (href && /^https:\/\//i.test(href)) chain().setLink({ href }).run();
        },
        editor.isActive('link'),
      ),
  };
}

/** The compact formatting bar shown above {@link InlineRichText} while it is focused. */
export default function InlineRichTextToolbar({
  editor,
  preset = 'textSection',
}: {
  editor: Editor;
  preset?: InlineRichTextPreset;
}) {
  const buttons = createButtons(editor);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        mb: 0.5,
        p: '3px',
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 2,
      }}
    >
      {INLINE_RICH_TEXT_PRESETS[preset].buttons.map((name) => buttons[name]())}
    </Box>
  );
}
